import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Sparkles, UserRoundPlus, X } from "lucide-react";
import { Button } from "@/components/Button";
import { Text } from "@/components/Typography";
import { SuggestedAdvisor } from "@/services/api";

const MAX_MESSAGE_LENGTH = 450;

interface InviteToPabModalProps {
  isOpen: boolean;
  advisor: SuggestedAdvisor | null;
  generatedSummary?: string | null;
  isSummaryLoading?: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: (advisor: SuggestedAdvisor, message: string) => void;
}

export const InviteToPabModal = ({
  isOpen,
  advisor,
  generatedSummary = null,
  isSummaryLoading = false,
  isLoading = false,
  onClose,
  onConfirm,
}: InviteToPabModalProps) => {
  const defaultMessage = useMemo(() => {
    const message = (generatedSummary || "").trim();
    return message.slice(0, MAX_MESSAGE_LENGTH);
  }, [generatedSummary]);
  const [message, setMessage] = useState(defaultMessage);

  useEffect(() => {
    if (isOpen) {
      setMessage(defaultMessage);
    }
  }, [defaultMessage, isOpen]);

  const messageCount = message.length;
  const progressWidth = Math.min((messageCount / MAX_MESSAGE_LENGTH) * 100, 100);

  const handleConfirm = () => {
    if (!advisor || isLoading) return;
    onConfirm(advisor, message.trim());
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        <Dialog.Content className="advisor-invite-modal-shell font-inter data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <div className="advisor-invite-modal-header">
            <div className="pr-3">
              <Dialog.Title className="advisor-invite-modal-title">
                Invite {advisor?.name ?? "Expert"} to your Personal Advisory Board
              </Dialog.Title>
              <Dialog.Description className="advisor-invite-modal-subtitle">
                Introduce yourself and what you&apos;re looking for
              </Dialog.Description>
            </div>

            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-[14px] p-2 text-secondary-500 dark:text-pab-dark-subtle-text transition-colors hover:bg-secondary-100 dark:hover:bg-surface-card-inner"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-4 p-6">
            <div className="advisor-invite-modal-tip ">
              <Sparkles className="h-4 w-4 shrink-0 text-primary-650" />
              <Text
                as="p"
                className="font-inter text-14 leading-5 tracking-[-0.15px] text-primary-700"
              >
                {isSummaryLoading
                  ? "Preparing a personalized AI draft based on your profile and expert context..."
                  : "Review and edit the AI-generated message before sending your invite."}
              </Text>
            </div>

            <div className="flex items-center justify-between gap-2">
              <label htmlFor="invite-message" className="advisor-invite-modal-label">
                Your Message
              </label>
              <Text as="span" className="advisor-invite-modal-counter">
                {isSummaryLoading ? "-- / 450" : `${messageCount} / ${MAX_MESSAGE_LENGTH}`}
              </Text>
            </div>

            {isSummaryLoading ? (
              <div
                className="advisor-invite-modal-message animate-pulse space-y-3"
                aria-hidden="true"
              >
                <div className="h-5 w-full rounded bg-secondary-150" />
                <div className="h-5 w-full rounded bg-secondary-150" />
                <div className="h-5 w-4/5 rounded bg-secondary-150" />
              </div>
            ) : (
              <textarea
                id="invite-message"
                className="advisor-invite-modal-message"
                value={message}
                disabled={isLoading}
                onChange={(event) => setMessage(event.target.value.slice(0, MAX_MESSAGE_LENGTH))}
              />
            )}

            <div className="advisor-invite-modal-progress-track">
              <div
                className="advisor-invite-modal-progress-fill"
                style={{ width: `${progressWidth}%` }}
              />
            </div>

            <div className="flex flex-col gap-3 pt-1 sm:flex-row">
              <Button
                type="button"
                variant="ghost"
                size="md"
                className="h-[54px] flex-1 rounded-[14px] border border-secondary-200 dark:border-surface-border bg-white dark:bg-surface-card-inner font-inter text-16 font-semibold leading-6 tracking-[-0.31px] text-secondary-900 dark:text-white hover:bg-secondary-50 dark:hover:bg-surface-card"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>

              <Button
                type="button"
                variant="primaryGradient"
                size="md"
                isLoading={isLoading}
                leftIcon={<UserRoundPlus className="h-[18px] w-[18px]" />}
                className="h-[54px] flex-1 rounded-[10px] font-inter text-16 font-semibold leading-6 tracking-[-0.31px] text-white"
                onClick={handleConfirm}
                disabled={isLoading || isSummaryLoading || !advisor || !message.trim()}
              >
                Invite to Advisory Board
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default InviteToPabModal;
